import { Text } from "@ttc3k/trekker";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { useOperatorByIdQuery } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { CollapsableStep } from "widgets/booker/shared";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { useOrgScopedUserOperators } from "widgets/booker/shared/hooks/useOrgScopedUserOperators";

export const OperatorSelect = () => {
  const { userOperators, booker, readOnlySelection, organizationId } =
    useBookerContext();
  const { selectedOperatorId, updateOperatorId } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "OPERATOR_SELECT" });

  const isTeamVisible =
    (
      booker as unknown as {
        teamVisible?: boolean;
      } | null
    )?.teamVisible === true;

  const { operators: scopedOperators, loading: scopedOperatorsLoading } =
    useOrgScopedUserOperators(
      userOperators,
      organizationId,
      isTeamVisible && !!organizationId,
    );

  const selectableOperators =
    organizationId && isTeamVisible ? scopedOperators : userOperators ?? [];

  const { data: lockedOperatorData } = useOperatorByIdQuery({
    variables: { operatorId: selectedOperatorId ?? "" },
    skip: !readOnlySelection || !selectedOperatorId,
  });

  if (readOnlySelection && selectedOperatorId) {
    const selectedOperator = userOperators?.find(
      operator => operator.entityId === selectedOperatorId,
    );
    const name =
      selectedOperator?.name?.trim() ||
      (lockedOperatorData?.operatorById
        ? getMultilingualString(lockedOperatorData.operatorById.name ?? {})
        : "") ||
      selectedOperatorId;
    return (
      <CollapsableStep title={t("TITLE")} complete>
        <Flex
          p="300"
          border="1px solid"
          borderColor="border.dark"
          borderRadius="200"
          alignItems="center"
          gap="200"
        >
          <Text visual="bodySemiBold" color="text.dark">
            {name}
          </Text>
        </Flex>
      </CollapsableStep>
    );
  }

  if (
    scopedOperatorsLoading ||
    !isTeamVisible ||
    !selectableOperators ||
    selectableOperators.length <= 1
  ) {
    return null;
  }

  return (
    <CollapsableStep
      title={t("TITLE")}
      complete={!!selectedOperatorId}
      stepId="operator-select"
    >
      <Text color="text.mid">{t("SUBTITLE")}</Text>
      <Flex flexDir="column" gap="200">
        {selectableOperators.map(op => {
          const isSelected = selectedOperatorId === op.entityId;
          return (
            <Flex
              key={op.entityId}
              p="300"
              border="1px solid"
              borderColor={isSelected ? "border.dark" : "border.light"}
              borderRadius="200"
              cursor="pointer"
              _hover={{ borderColor: "border.mid" }}
              onClick={() => updateOperatorId(op.entityId)}
              alignItems="center"
              gap="200"
            >
              <Flex
                w="20px"
                h="20px"
                borderRadius="50%"
                border="2px solid"
                borderColor={isSelected ? "border.dark" : "border.light"}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                {isSelected && (
                  <Flex
                    w="10px"
                    h="10px"
                    borderRadius="50%"
                    bg="text.dark"
                  />
                )}
              </Flex>
              <Text
                visual="bodySemiBold"
                color={isSelected ? "text.dark" : "text.mid"}
              >
                {op.name}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </CollapsableStep>
  );
};
